import { useEffect, useMemo, useState } from 'react';
import { useReactFlow } from '@xyflow/react';
import {
  ChevronDown,
  X,
  Check,
  TriangleAlert,
  CircleAlert,
  Crosshair,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { FileIconTile } from '@/pages/workflow/component/file-icon-tile';
import {
  getFileIcon,
  getFileIconBackgroundColor,
} from '@/constants/node-styling';
import {
  useUploadStore,
  UploadItem,
  isActivePhase,
} from '@/stores/use-upload-store';
import { UploadBar, formatUploadBytes } from './upload-ui';

const RING_RADIUS = 15;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const ProgressRing = ({ percent }: { percent: number }) => {
  const offset =
    RING_CIRCUMFERENCE * (1 - Math.max(0, Math.min(100, percent)) / 100);
  return (
    <svg viewBox='0 0 36 36' className='size-6 -rotate-90'>
      <circle
        cx='18'
        cy='18'
        r={RING_RADIUS}
        fill='none'
        strokeWidth='4'
        className='stroke-primary/20'
      />
      <circle
        cx='18'
        cy='18'
        r={RING_RADIUS}
        fill='none'
        strokeWidth='4'
        strokeLinecap='round'
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={offset}
        className='stroke-primary transition-[stroke-dashoffset] duration-300 ease-out'
      />
    </svg>
  );
};

const NodeLabel = ({ item }: { item: UploadItem }) => (
  <div className='mt-0.5 flex items-center gap-1 text-[10.5px] text-muted-foreground'>
    <Crosshair className='size-2.5 shrink-0' />
    <span className='truncate'>{item.nodeName || 'File Input'}</span>
  </div>
);

export const UploadProgressWidget = () => {
  const uploads = useUploadStore((state) => state.uploads);
  const cancelUpload = useUploadStore((state) => state.cancelUpload);
  const removeUpload = useUploadStore((state) => state.removeUpload);
  const dismissResolved = useUploadStore((state) => state.dismissResolved);
  const { fitView } = useReactFlow();

  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const items = useMemo(() => Object.values(uploads), [uploads]);
  const active = useMemo(
    () => items.filter((item) => isActivePhase(item.phase)),
    [items],
  );
  const failed = useMemo(
    () => items.filter((item) => item.phase === 'failed'),
    [items],
  );
  const done = useMemo(
    () => items.filter((item) => item.phase === 'done'),
    [items],
  );

  // A fresh upload re-opens the widget after a previous dismiss.
  useEffect(() => {
    if (active.length > 0) setDismissed(false);
  }, [active.length]);

  const focusNode = (nodeId: string) => {
    fitView({ nodes: [{ id: nodeId }], duration: 400, maxZoom: 1.2 });
  };

  const aggregate = useMemo(() => {
    const tracked = [...active, ...done];
    const totalBytes = tracked.reduce((sum, item) => sum + item.size, 0);
    const loadedBytes = tracked.reduce(
      (sum, item) => sum + (item.phase === 'done' ? item.size : item.loaded),
      0,
    );
    const percent =
      totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 0;
    return { totalBytes, loadedBytes, percent };
  }, [active, done]);

  if (items.length === 0 || dismissed) return null;

  const hasActive = active.length > 0;
  const hasFailed = failed.length > 0;

  return (
    <div className='pointer-events-auto absolute bottom-6 right-6 z-20 w-[328px] overflow-hidden rounded-[10px] border border-border bg-card shadow-xl'>
      {hasActive ? (
        <>
          <div className='flex items-center gap-2.5 py-3 pl-3.5 pr-3'>
            <ProgressRing percent={aggregate.percent} />
            <div className='min-w-0 flex-1'>
              <div className='text-[13.5px] font-semibold'>
                Uploading {active.length} file{active.length === 1 ? '' : 's'}
              </div>
              <div className='mt-0.5 text-[11px] text-muted-foreground'>
                {aggregate.percent}% ·{' '}
                {formatUploadBytes(aggregate.loadedBytes)} of{' '}
                {formatUploadBytes(aggregate.totalBytes)}
              </div>
            </div>
            <Button
              variant='ghost'
              size='icon'
              className='size-7 text-muted-foreground'
              title={expanded ? 'Collapse' : 'Expand'}
              onClick={() => setExpanded((value) => !value)}
            >
              <ChevronDown
                className={cn(
                  'size-4 transition-transform duration-200',
                  expanded ? '' : '-rotate-180',
                )}
              />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              className='size-7 text-muted-foreground'
              title='Hide'
              onClick={() => setDismissed(true)}
            >
              <X className='size-4' />
            </Button>
          </div>
          <div className='px-3.5 pb-3'>
            <UploadBar
              percent={aggregate.percent}
              indeterminate={aggregate.percent === 0}
            />
          </div>
          {expanded && (
            <div className='flex flex-col gap-0.5 border-t border-border p-1.5'>
              {[...active, ...failed].map((item) => (
                <UploadRow
                  key={item.uploadId}
                  item={item}
                  onCancel={() => cancelUpload(item.uploadId)}
                  onDismiss={() => removeUpload(item.uploadId)}
                  onFocus={() => focusNode(item.nodeId)}
                />
              ))}
            </div>
          )}
        </>
      ) : hasFailed ? (
        <>
          <div className='flex items-center gap-2.5 border-b border-border py-3 pl-3.5 pr-3'>
            <span className='flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-500'>
              <TriangleAlert className='size-3.5' />
            </span>
            <div className='min-w-0 flex-1'>
              <div className='text-[13.5px] font-semibold'>
                {done.length} of {done.length + failed.length} uploaded
              </div>
              <div className='mt-0.5 text-[11px] text-muted-foreground'>
                {failed.length} file{failed.length === 1 ? '' : 's'} failed
              </div>
            </div>
            <Button
              variant='ghost'
              size='icon'
              className='size-7 text-muted-foreground'
              title='Dismiss'
              // Only hide the widget — keep failed uploads in the store so the
              // node row and Configure panel retain their inline error state.
              onClick={() => setDismissed(true)}
            >
              <X className='size-4' />
            </Button>
          </div>
          <div className='flex flex-col gap-0.5 p-1.5'>
            {[...done, ...failed].map((item) => (
              <UploadResultRow
                key={item.uploadId}
                item={item}
                onFocus={() => focusNode(item.nodeId)}
              />
            ))}
          </div>
        </>
      ) : (
        <div className='flex items-center gap-2.5 p-3.5'>
          <span className='flex size-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500'>
            <Check className='size-4' strokeWidth={2.5} />
          </span>
          <div className='min-w-0 flex-1'>
            <div className='text-[13.5px] font-semibold'>
              {done.length} upload{done.length === 1 ? '' : 's'} complete
            </div>
            <div className='mt-0.5 text-[11px] text-muted-foreground'>
              Files are ready for downstream nodes
            </div>
          </div>
          <Button
            variant='outline'
            size='sm'
            className='h-7 px-3 text-xs'
            onClick={() => dismissResolved()}
          >
            Close
          </Button>
        </div>
      )}
    </div>
  );
};

interface UploadRowProps {
  item: UploadItem;
  onCancel: () => void;
  onDismiss: () => void;
  onFocus: () => void;
}

const UploadRow = ({ item, onCancel, onDismiss, onFocus }: UploadRowProps) => {
  const isFailed = item.phase === 'failed';
  const isProcessing = item.phase === 'processing';
  return (
    <div
      role='button'
      tabIndex={0}
      className='flex cursor-pointer items-center gap-2.5 rounded-lg p-2 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50'
      onClick={onFocus}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onFocus();
        }
      }}
    >
      {isFailed ? (
        <span className='flex size-7 shrink-0 items-center justify-center rounded-lg bg-destructive/15 text-destructive'>
          <CircleAlert className='size-3.5' />
        </span>
      ) : (
        <FileIconTile
          icon={getFileIcon(item.filename)}
          backgroundColor={getFileIconBackgroundColor(item.filename)}
          size='small'
          iconColor='#ffffff'
        />
      )}
      <div className='min-w-0 flex-1'>
        <div className='truncate text-xs font-semibold' title={item.filename}>
          {item.filename}
        </div>
        {isFailed ? (
          <div className='mt-0.5 truncate text-[10.5px] text-destructive'>
            {item.error || 'Upload failed'}
          </div>
        ) : (
          <div className='mt-1 flex items-center gap-1.5'>
            <UploadBar
              percent={item.percent}
              indeterminate={isProcessing || item.percent === 0}
              className='flex-1'
            />
            <span className='font-mono text-[10px] font-semibold tabular-nums text-primary'>
              {isProcessing
                ? 'Finishing'
                : item.percent === 0
                  ? 'Starting'
                  : `${item.percent}%`}
            </span>
          </div>
        )}
        <NodeLabel item={item} />
      </div>
      <Button
        variant='ghost'
        size='icon'
        className='size-6 shrink-0 text-muted-foreground'
        title={isFailed ? 'Dismiss' : 'Cancel'}
        onClick={(e) => {
          e.stopPropagation();
          if (isFailed) {
            onDismiss();
          } else {
            onCancel();
          }
        }}
      >
        <X className='size-3.5' />
      </Button>
    </div>
  );
};

interface UploadResultRowProps {
  item: UploadItem;
  onFocus: () => void;
}

const UploadResultRow = ({ item, onFocus }: UploadResultRowProps) => {
  const isFailed = item.phase === 'failed';
  return (
    <div
      role='button'
      tabIndex={0}
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-lg p-2 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
        isFailed && 'bg-destructive/5',
      )}
      onClick={onFocus}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onFocus();
        }
      }}
    >
      {isFailed ? (
        <span className='flex size-7 shrink-0 items-center justify-center rounded-lg bg-destructive/15 text-destructive'>
          <CircleAlert className='size-3.5' />
        </span>
      ) : (
        <FileIconTile
          icon={getFileIcon(item.filename)}
          backgroundColor={getFileIconBackgroundColor(item.filename)}
          size='small'
          iconColor='#ffffff'
        />
      )}
      <div className='min-w-0 flex-1'>
        <div className='truncate text-xs font-semibold' title={item.filename}>
          {item.filename}
        </div>
        {isFailed ? (
          <div className='mt-0.5 truncate text-[10.5px] text-destructive'>
            {item.error || 'Upload failed'}
          </div>
        ) : (
          <NodeLabel item={item} />
        )}
      </div>
      {isFailed ? (
        <span className='flex shrink-0 items-center gap-1 text-[11px] font-medium text-destructive'>
          <CircleAlert className='size-3' />
          Failed
        </span>
      ) : (
        <span className='flex shrink-0 items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-500'>
          <Check className='size-3' strokeWidth={2.5} />
          Done
        </span>
      )}
    </div>
  );
};
